import React, { useState } from 'react';
import { tilfoejMaerkeDag } from '../../services/firestoreService';
import { useToast } from '../shared/Toast';
import Button from '../shared/Button';
import { DANSKE_MAANEDER } from '../../utils/dateUtils';
import styles from './Form.module.scss';

const TilfoejMaerkeDagForm: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const [titel, setTitel] = useState('');
  const [dag, setDag] = useState('1');
  const [maaned, setMaaned] = useState('1');
  const [gentagelse, setGentagelse] = useState<'aarlig' | 'engangs'>('aarlig');
  const [aar, setAar] = useState(String(new Date().getFullYear()));
  const [beskrivelse, setBeskrivelse] = useState('');
  const [vej, setVej] = useState('Askevej');
  const [vejnummer, setVejnummer] = useState('');
  const [oprettetAf, setOprettetAf] = useState('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i - 2);

  const daysInMonth = new Date(
    parseInt(aar),
    parseInt(maaned),
    0
  ).getDate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) return;

    setSubmitting(true);
    try {
      let dato: string;
      if (gentagelse === 'aarlig') {
        dato = `${String(maaned).padStart(2, '0')}-${String(dag).padStart(2, '0')}`;
      } else {
        dato = `${aar}-${String(maaned).padStart(2, '0')}-${String(dag).padStart(2, '0')}`;
      }

      await tilfoejMaerkeDag({
        titel: titel.trim(),
        dato,
        gentagelse,
        type: 'maerkedag',
        beskrivelse: beskrivelse.trim() || undefined,
        vej: vej.trim() || undefined,
        vejnummer: vejnummer.trim() || undefined,
        oprettetAf: oprettetAf.trim() || undefined,
      });

      showSuccess('Mærkedagen er blevet tilføjet!');
      // Reset form
      setTitel('');
      setDag('1');
      setMaaned('1');
      setGentagelse('aarlig');
      setAar(String(currentYear));
      setBeskrivelse('');
      setVej('Askevej');
      setVejnummer('');
      setOprettetAf('');
    } catch (err) {
      console.error(err);
      showError('Noget gik galt. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.field}>
        <label htmlFor="md-titel" className={styles.label}>
          Titel <span className={styles.required}>*</span>
        </label>
        <input
          id="md-titel"
          type="text"
          className={styles.input}
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          maxLength={100}
          placeholder="Fx. Vores bys jubilæum"
          required
        />
        <span className={styles.charCount}>{titel.length}/100</span>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Dato</label>
        <div className={styles.dateRow}>
          <div className={styles.selectWrapper}>
            <label htmlFor="md-dag" className={styles.sublabel}>Dag</label>
            <input
              id="md-dag"
              type="number"
              className={styles.input}
              value={dag}
              min={1}
              max={daysInMonth}
              onChange={(e) => setDag(e.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className={styles.selectWrapper}>
            <label htmlFor="md-maaned" className={styles.sublabel}>Måned</label>
            <select
              id="md-maaned"
              className={styles.select}
              value={maaned}
              onChange={(e) => setMaaned(e.target.value)}
            >
              {DANSKE_MAANEDER.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Gentagelse</label>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="gentagelse"
              value="aarlig"
              checked={gentagelse === 'aarlig'}
              onChange={() => setGentagelse('aarlig')}
              className={styles.radio}
            />
            <span>Gentages hvert år</span>
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="gentagelse"
              value="engangs"
              checked={gentagelse === 'engangs'}
              onChange={() => setGentagelse('engangs')}
              className={styles.radio}
            />
            <span>Engangsbegivenhed</span>
          </label>
        </div>
      </div>

      {gentagelse === 'engangs' && (
        <div className={styles.field}>
          <label htmlFor="md-aar" className={styles.label}>År</label>
          <select
            id="md-aar"
            className={styles.select}
            value={aar}
            onChange={(e) => setAar(e.target.value)}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.field}>
        <label htmlFor="md-beskrivelse" className={styles.label}>
          Beskrivelse <span className={styles.optional}>(valgfri)</span>
        </label>
        <textarea
          id="md-beskrivelse"
          className={styles.textarea}
          value={beskrivelse}
          onChange={(e) => setBeskrivelse(e.target.value)}
          placeholder="Fortæl lidt om dagen..."
          rows={3}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Adresse <span className={styles.optional}>(valgfri)</span></label>
        <div className={styles.dateRow}>
          <div className={styles.selectWrapper} style={{ flex: 2 }}>
            <label htmlFor="md-vej" className={styles.sublabel}>Vejnavn</label>
            <input
              id="md-vej"
              type="text"
              className={styles.input}
              value={vej}
              onChange={(e) => setVej(e.target.value)}
              placeholder="Fx. Askevej"
            />
          </div>
          <div className={styles.selectWrapper} style={{ flex: 1 }}>
            <label htmlFor="md-vejnummer" className={styles.sublabel}>Nummer</label>
            <input
              id="md-vejnummer"
              type="text"
              className={styles.input}
              value={vejnummer}
              onChange={(e) => setVejnummer(e.target.value)}
              placeholder="Fx. 12"
            />
          </div>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="md-navn" className={styles.label}>
          Dit navn <span className={styles.optional}>(valgfri)</span>
        </label>
        <input
          id="md-navn"
          type="text"
          className={styles.input}
          value={oprettetAf}
          onChange={(e) => setOprettetAf(e.target.value)}
          placeholder="Fx. Familie Hansen"
        />
      </div>

      <Button type="submit" disabled={submitting || !titel.trim()} fullWidth>
        {submitting ? 'Tilføjer...' : 'Tilføj mærkedag'}
      </Button>
    </form>
  );
};

export default TilfoejMaerkeDagForm;
